/**
 * 用途：预算管理页，展示各广告账户的预算分配与消耗情况，支持预算调整
 * 路由：/budgets
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { MultiSelect } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import {
  budgetsApi,
  type AccountOption,
  type AlertEvent,
  type AlertRule,
  type BudgetAllocation,
  type BudgetTransfer,
  type ChangeLog,
  type ChannelAllocation,
  type ChannelOption,
  type StrategyEffect,
  type StrategySuggestion,
} from '../api/budgets';
import { useAppStore } from '../store/useAppStore';
import { sf as fieldSf } from '../lib/fieldPolicy';
import { TableLoadingCell } from '../components/TableLoadingOverlay';
type TabKey = 'allocation' | 'channel' | 'alert' | 'strategy' | 'log';
type ToastType = 'success' | 'error' | 'warning' | 'info';
type ToastItem = { id: number; message: string; type: ToastType };

const TOAST_COLOR: Record<ToastType, { border: string; color: string; bg: string }> = {
  success: { border: 'rgba(34,197,94,.35)',  color: 'var(--green)', bg: 'rgba(34,197,94,.06)'  },
  error:   { border: 'rgba(239,68,68,.35)',   color: 'var(--red)',   bg: 'rgba(239,68,68,.06)'   },
  warning: { border: 'rgba(245,158,11,.35)',  color: 'var(--amber)', bg: 'rgba(245,158,11,.06)'  },
  info:    { border: 'var(--border2)',         color: 'var(--text)',  bg: 'var(--surface2)'       },
};
function ToastIcon({ type }: { type: ToastType }) {
  const s: React.CSSProperties = { width: 16, height: 16, flexShrink: 0 };
  if (type === 'success') return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>;
  if (type === 'error')   return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
  if (type === 'warning') return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
  return <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
}
type FormState = { optimizer: string; month: string; daily_budget: string; monthly_budget: string; note: string };
type ChannelFormState = { optimizer: string; channel: string; month: string; daily_budget: string; monthly_budget: string; roi: string; cpa: string };
const channelName: Record<string, string> = { google: 'Google', facebook: 'Facebook', tiktok: 'TikTok' };
const ALL_CHANNELS = ['facebook', 'google', 'tiktok'];

const pct = (spent: number, budget: number) => (budget > 0 ? Math.round((spent / budget) * 100) : 0);
const pctColor = (p: number) => (p >= 100 ? 'var(--red)' : p >= 90 ? 'rgb(249,115,22)' : p >= 70 ? 'var(--amber)' : 'var(--green)');
const money = (v: number) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const toYearMonth = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const currentMonth = () => toYearMonth(new Date());
const emptyForm = (): FormState => ({ optimizer: '', month: currentMonth(), daily_budget: '', monthly_budget: '', note: '' });

const defaultRule: AlertRule = {
  id: '',
  user_id: '',
  brand_id: '',
  level1_threshold: 70,
  level2_threshold: 90,
  level3_threshold: 100,
  level1_channels: ['站内信'],
  level2_channels: ['站内信', 'Slack'],
  level3_channels: ['站内信', 'Slack', '邮件'],
  auto_pause: true,
  channel_level_enabled: true,
  quiet_start: '',
  quiet_end: '',
};

export default function Budgets() {
  const { brandId, siteId, hasPermission, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const sfFull = (v: number) => fieldSf(`$${v.toFixed(2)}`, 'budget', userLevel);
  const sf = (v: number) => sfFull(v).value ?? '$*';
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<TabKey>('allocation');

  const toastIdRef = useRef(0);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const dismissToast = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));
  const showToast = (message: string, type: ToastType = 'info') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev.slice(-2), { id, message, type }]);
    const ttl = type === 'error' ? 6000 : type === 'warning' ? 5000 : 3500;
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), ttl);
  };

  const [list, setList] = useState<BudgetAllocation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalPage, setTotalPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<BudgetAllocation | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingID, setDeletingID] = useState<string | null>(null);
  const [confirmDeleteID, setConfirmDeleteID] = useState<string | null>(null);

  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);

  const [channelOptimizer, setChannelOptimizer] = useState('');
  const [channelAllocations, setChannelAllocations] = useState<ChannelAllocation[]>([]);
  const [showChannelAddModal, setShowChannelAddModal] = useState(false);
  const [channelForm, setChannelForm] = useState<ChannelFormState>({ optimizer: '', channel: '', month: '', daily_budget: '', monthly_budget: '', roi: '0', cpa: '0' });
  const [channelSaving, setChannelSaving] = useState(false);
  const [confirmDeleteChannelIdx, setConfirmDeleteChannelIdx] = useState<number | null>(null);
  const [transferList, setTransferList] = useState<BudgetTransfer[]>([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({ optimizer: '', from_channel: '', to_channel: '', amount: '0', reason: '' });
  const [transferChannels, setTransferChannels] = useState<ChannelOption[]>([]);
  const [channelAllocAll, setChannelAllocAll] = useState<ChannelAllocation[]>([]);

  const [alertRule, setAlertRule] = useState<AlertRule>(defaultRule);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [currentAlerts, setCurrentAlerts] = useState<AlertEvent[]>([]);
  const [historyAlerts, setHistoryAlerts] = useState<AlertEvent[]>([]);
  const [alertFilterLevel, setAlertFilterLevel] = useState('');

  const [strategyMode, setStrategyMode] = useState('roi');
  const [strategies, setStrategies] = useState<StrategySuggestion[]>([]);
  const [effects, setEffects] = useState<StrategyEffect[]>([]);

  const [logType, setLogType] = useState('');
  const [logs, setLogs] = useState<ChangeLog[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logSize, setLogSize] = useState(20);
  const [logTotalPage, setLogTotalPage] = useState(0);
  const [activeMonth, setActiveMonth] = useState(currentMonth);

  const monthOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return toYearMonth(d);
    });
  }, []);

  const fetchAccountOptions = async () => {
    try {
      const res = await budgetsApi.getAccountOptions(brandId, siteId);
      setAccountOptions(res.list || []);
    } catch {
      setAccountOptions([]);
    }
  };

  const fetchAllocation = async () => {
    setLoading(true);
    try {
      const res = await budgetsApi.list({ brand_id: brandId, site_id: siteId, page, size, search: search.trim() || undefined, month: activeMonth });
      setList(res.list || []);
      setTotal(res.total || 0);
      setTotalPage(res.total_page || 0);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '加载预算数据失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchChannel = async () => {
    const [allocRes, allocAllRes, budgetRes, transferRes] = await Promise.all([
      budgetsApi.listChannelAllocations(brandId, channelOptimizer || undefined, activeMonth, siteId),
      budgetsApi.listChannelAllocations(brandId, undefined, activeMonth, siteId),
      budgetsApi.list({ brand_id: brandId, site_id: siteId, page: 1, size: 200, month: activeMonth }),
      budgetsApi.listTransfers(brandId, undefined, activeMonth, siteId),
    ]);
    setChannelAllocations(allocRes.list || []);
    setChannelAllocAll(allocAllRes.list || []);
    setList(budgetRes.list || []);
    setTransferList(transferRes.list || []);
  };

  const fetchAlert = async () => {
    const [ruleRes, currentRes, historyRes] = await Promise.all([
      budgetsApi.getAlertRule(brandId, siteId),
      budgetsApi.listAlertEvents(brandId, 'current', undefined, undefined, siteId),
      budgetsApi.listAlertEvents(brandId, 'history', alertFilterLevel || undefined, activeMonth, siteId),
    ]);
    setAlertRule(ruleRes);
    setCurrentAlerts(currentRes.list || []);
    setHistoryAlerts(historyRes.list || []);
  };

  const fetchStrategy = async () => {
    const [sRes, eRes] = await Promise.all([
      budgetsApi.listStrategies(brandId, strategyMode, activeMonth, siteId),
      budgetsApi.listStrategyEffects(brandId, siteId),
    ]);
    setStrategies(sRes.list || []);
    setEffects(eRes.list || []);
  };

  const fetchLogs = async () => {
    const res = await budgetsApi.listChangeLogs(brandId, { action_type: logType || undefined, page: logPage, size: logSize, month: activeMonth }, siteId);
    setLogs(res.list || []);
    setLogTotal(res.total || 0);
    setLogTotalPage(res.total_page || 0);
  };

  useEffect(() => {
    void fetchAccountOptions();
  }, [brandId, siteId]);

  useEffect(() => {
    if (activeTab === 'allocation') void fetchAllocation();
  }, [activeTab, brandId, siteId, page, size, activeMonth]);

  useEffect(() => {
    if (activeTab === 'channel') void fetchChannel();
    if (activeTab === 'alert') void fetchAlert();
    if (activeTab === 'strategy') { void fetchStrategy(); void fetchChannel(); }
    if (activeTab === 'log') void fetchLogs();
  }, [activeTab, brandId, siteId, channelOptimizer, alertFilterLevel, strategyMode, logType, logPage, logSize, activeMonth]);

  useEffect(() => {
    if (!transferForm.optimizer || !showTransferModal) {
      setTransferChannels([]);
      return;
    }
    const allocs = channelAllocAll.filter((a) => a.optimizer === transferForm.optimizer);
    const channels: ChannelOption[] = allocs.map((a) => ({
      channel: a.channel,
      monthly_budget: a.monthly_budget,
      month_spent: a.month_spent,
      available: Math.max(0, a.monthly_budget - a.month_spent),
    }));
    setTransferChannels(channels);
    setTransferForm((f) => ({
      ...f,
      from_channel: channels.length > 0 ? channels[0].channel : '',
      to_channel: channels.length > 1 ? channels[1].channel : (channels.length > 0 ? channels[0].channel : ''),
    }));
  }, [transferForm.optimizer, showTransferModal, channelAllocAll]);

  const stats = useMemo(() => {
    const totalMonthBudget = list.reduce((s, it) => s + Number(it.monthly_budget || 0), 0);
    const totalMonthSpent = list.reduce((s, it) => s + Number(it.month_spent || 0), 0);
    const totalTodaySpent = list.reduce((s, it) => s + Number(it.today_spent || 0), 0);
    const alertCount = list.filter((it) => Number(it.alert_level) > 0).length;
    return { totalMonthBudget, totalMonthSpent, totalTodaySpent, alertCount };
  }, [list]);

  const channelByOptimizer = useMemo(() => {
    const m = new Map<string, ChannelAllocation[]>();
    channelAllocations.forEach((it) => {
      const arr = m.get(it.optimizer) || [];
      arr.push(it);
      m.set(it.optimizer, arr);
    });
    return m;
  }, [channelAllocations]);

  const channelAllocOptimizers = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    channelAllocAll.forEach((a) => {
      if (!seen.has(a.optimizer)) { seen.add(a.optimizer); result.push(a.optimizer); }
    });
    return result;
  }, [channelAllocAll]);

  const openCreate = () => {
    if (activeTab === 'channel') {
      setChannelForm({ optimizer: channelOptimizer, channel: '', month: activeMonth, daily_budget: '', monthly_budget: '', roi: '0', cpa: '0' });
      setShowChannelAddModal(true);
    } else {
      setEditing(null);
      setForm(emptyForm());
      setShowModal(true);
    }
  };
  const openEdit = (item: BudgetAllocation) => {
    setEditing(item);
    setForm({
      optimizer: item.optimizer,
      month: item.month || activeMonth,
      daily_budget: String(item.daily_budget),
      monthly_budget: String(item.monthly_budget),
      note: item.note || '',
    });
    setShowModal(true);
  };

  const submitAllocation = async () => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行预算调整', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:budgets:adjust')) {
      showToast('暂无预算调整权限', 'error');
      return;
    }
    const payload = {
      optimizer: form.optimizer.trim(),
      month: form.month.trim() || activeMonth,
      daily_budget: Number(form.daily_budget),
      monthly_budget: Number(form.monthly_budget),
      note: form.note.trim(),
    };
    if (!payload.optimizer || payload.daily_budget <= 0 || payload.monthly_budget <= 0) {
      showToast('请填写优化师、日预算、月预算，且预算必须大于 0', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editing) await budgetsApi.update(editing.id, payload, brandId, siteId);
      else await budgetsApi.create(payload, brandId, siteId);
      setShowModal(false);
      await fetchAllocation();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDeleteID) return;
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行预算调整', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:budgets:adjust')) {
      showToast('暂无预算调整权限', 'error');
      return;
    }
    const id = confirmDeleteID;
    setConfirmDeleteID(null);
    setDeletingID(id);
    try {
      await budgetsApi.remove(id, brandId, siteId);
      await fetchAllocation();
    } finally {
      setDeletingID(null);
    }
  };

  const saveChannel = async () => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行预算调整', 'error');
      return;
    }
    const groups = new Map<string, ChannelAllocation[]>();
    channelAllocations.forEach((x) => {
      const arr = groups.get(x.optimizer) ?? [];
      arr.push(x);
      groups.set(x.optimizer, arr);
    });
    const toSave = channelOptimizer
      ? ([[channelOptimizer, groups.get(channelOptimizer) ?? []]] as [string, ChannelAllocation[]][])
      : (Array.from(groups.entries()) as [string, ChannelAllocation[]][]);
    try {
      for (const [optimizer, allocs] of toSave) {
        if (allocs.length === 0) continue;
        await budgetsApi.saveChannelAllocations(brandId, {
          optimizer,
          month: activeMonth,
          allocations: allocs.map((x) => ({
            channel: x.channel,
            daily_budget: Number(x.daily_budget || 0),
            monthly_budget: Number(x.monthly_budget || 0),
            roi: Number(x.roi || 0),
            cpa: Number(x.cpa || 0),
          })),
        }, siteId);
      }
      await fetchChannel();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败', 'error');
    }
  };

  const addChannelRow = async () => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行预算调整', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:budgets:adjust')) {
      showToast('暂无预算调整权限', 'error');
      return;
    }
    const optimizer = channelForm.optimizer.trim();
    const channel = channelForm.channel.trim();
    const dailyBudget = Number(channelForm.daily_budget || 0);
    const monthlyBudget = Number(channelForm.monthly_budget || 0);
    const roi = Number(channelForm.roi || 0);
    const cpa = Number(channelForm.cpa || 0);

    if (!optimizer) { showToast('请选择优化师', 'error'); return; }
    if (!channel) { showToast('请选择渠道', 'error'); return; }
    if (monthlyBudget <= 0) { showToast('月预算必须大于 0', 'error'); return; }
    if (dailyBudget <= 0) { showToast('日预算必须大于 0', 'error'); return; }
    if (dailyBudget > monthlyBudget) { showToast('日预算不能大于月预算', 'error'); return; }

    const month = channelForm.month || activeMonth;
    const budgetAlloc = list.find((x) => x.optimizer === optimizer);
    if (!budgetAlloc) { showToast(`请先为该优化师在 ${month} 设置预算分配`, 'error'); return; }

    const existing = channelAllocations.filter((x) => x.optimizer === optimizer);
    if (existing.find((x) => x.channel === channel)) { showToast(`该优化师 ${month} 已有 ${channel} 渠道的分配记录，不能重复添加`, 'error'); return; }

    const sumMonthly = existing.reduce((s, x) => s + Number(x.monthly_budget), 0) + monthlyBudget;
    if (sumMonthly > Number(budgetAlloc.monthly_budget) + 0.01) {
      showToast(`渠道月预算合计 $${sumMonthly.toFixed(2)} 将超过总月预算 $${Number(budgetAlloc.monthly_budget).toFixed(2)}`, 'error');
      return;
    }
    const sumDaily = existing.reduce((s, x) => s + Number(x.daily_budget), 0) + dailyBudget;
    if (sumDaily > Number(budgetAlloc.daily_budget) + 0.01) {
      showToast(`渠道日预算合计 $${sumDaily.toFixed(2)} 将超过总日预算 $${Number(budgetAlloc.daily_budget).toFixed(2)}`, 'error');
      return;
    }

    setChannelSaving(true);
    try {
      const allocs = [
        ...existing.map((x) => ({ channel: x.channel, daily_budget: Number(x.daily_budget), monthly_budget: Number(x.monthly_budget), roi: Number(x.roi), cpa: Number(x.cpa) })),
        { channel, daily_budget: dailyBudget, monthly_budget: monthlyBudget, roi, cpa },
      ];
      await budgetsApi.saveChannelAllocations(brandId, { optimizer, month: channelForm.month || activeMonth, allocations: allocs }, siteId);
      setShowChannelAddModal(false);
      setChannelForm({ optimizer: '', channel: '', month: activeMonth, daily_budget: '', monthly_budget: '', roi: '0', cpa: '0' });
      await fetchChannel();
      await fetchAllocation();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败', 'error');
    } finally {
      setChannelSaving(false);
    }
  };

  const deleteChannelRow = async (idx: number) => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行预算调整', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:budgets:adjust')) {
      showToast('暂无预算调整权限', 'error');
      return;
    }
    const row = channelAllocations[idx];
    const remaining = channelAllocations.filter((x, i) => i !== idx && x.optimizer === row.optimizer);
    try {
      await budgetsApi.saveChannelAllocations(brandId, {
        optimizer: row.optimizer,
        month: row.month || activeMonth,
        allocations: remaining.map((x) => ({
          channel: x.channel,
          daily_budget: Number(x.daily_budget),
          monthly_budget: Number(x.monthly_budget),
          roi: Number(x.roi),
          cpa: Number(x.cpa),
        })),
      }, siteId);
      setConfirmDeleteChannelIdx(null);
      await fetchChannel();
      await fetchAllocation();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败', 'error');
      setConfirmDeleteChannelIdx(null);
    }
  };

  const createTransfer = async () => {
    const amount = Number(transferForm.amount || 0);
    const fromOpt = transferChannels.find((c) => c.channel === transferForm.from_channel);
    if (fromOpt && fromOpt.monthly_budget > 0 && amount > fromOpt.available) {
      showToast(`调拨金额超过 ${transferForm.from_channel} 渠道可用余额 ${money(fromOpt.available)}`, 'error');
      return;
    }
    try {
      await budgetsApi.createTransfer(brandId, {
        optimizer: transferForm.optimizer,
        from_channel: transferForm.from_channel,
        to_channel: transferForm.to_channel,
        amount,
        reason: transferForm.reason,
      }, siteId);
      setShowTransferModal(false);
      setTransferForm({ optimizer: '', from_channel: '', to_channel: '', amount: '0', reason: '' });
      setTransferChannels([]);
      await fetchChannel();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '提交失败', 'error');
    }
  };

  const updateTransferStatus = async (id: string, status: string) => {
    await budgetsApi.updateTransferStatus(brandId, id, status, siteId);
    await fetchChannel();
  };

  const saveAlertRule = async () => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法修改预警规则', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:budgets:adjust')) {
      showToast('暂无预算调整权限', 'error');
      return;
    }
    await budgetsApi.updateAlertRule(brandId, alertRule, siteId);
    setShowRuleModal(false);
    await fetchAlert();
  };

  const adoptStrategy = async (id: string) => {
    await budgetsApi.adoptStrategy(brandId, id, siteId);
    await fetchStrategy();
  };

  const channelTag = (ch: string) => {
    const key = ch.toLowerCase();
    const cls = key === 'google' ? 'gg' : key === 'facebook' ? 'fb' : 'tt';
    return <span className={`ch-tag ${cls}`}>{channelName[key] || ch}</span>;
  };

  const actionTypeText = (v: string) => {
    if (v === 'adjust') return '预算调整';
    if (v === 'transfer') return '渠道调拨';
    if (v === 'strategy') return '策略生成';
    if (v === 'alert') return '预警触发';
    if (v === 'freeze') return '恢复投放';
    return v;
  };

  const actionTypeColor = (v: string) => {
    if (v === 'adjust') return 'var(--amber)';
    if (v === 'transfer') return 'var(--primary-light)';
    if (v === 'strategy') return 'var(--cyan)';
    if (v === 'alert') return 'var(--red)';
    if (v === 'freeze') return 'var(--green)';
    return 'var(--text2)';
  };

  return (
    <div className="page-section visible">
      {isMobile ? (
        <div className="toolbar-mobile">
          <div className="toolbar-mobile-row tabs-row">
            <div className="page-tabs">
              {[
                ['allocation', '预算分配'],
                ['channel', '跨渠道分配'],
                ['alert', '分级预警'],
                ['strategy', '策略建议'],
                ['log', '变更日志'],
              ].map(([k, label]) => (
                <div key={k} className={`page-tab ${activeTab === k ? 'active' : ''}`} onClick={() => setActiveTab(k as TabKey)}>
                  {label}
                </div>
              ))}
            </div>
          </div>
          <div className="toolbar-mobile-row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0 }}>
              <MultiSelect single options={monthOptions.map((m, i) => ({ value: m, label: i === 0 ? `当前月 (${m})` : i === 1 ? `上月 (${m})` : m }))} value={[activeMonth]} onChange={(vals) => { setActiveMonth(vals[0] ?? monthOptions[0] ?? ''); setPage(1); }} style={{ flex: 1, minWidth: 0 }} />
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm">导出报表</button>
              <button className="btn btn-primary btn-sm" onClick={openCreate}>{activeTab === 'channel' ? '新增渠道分配' : '设置预算'}</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="toolbar">
          <div className="page-tabs">
            {[
              ['allocation', '预算分配'],
              ['channel', '跨渠道分配'],
              ['alert', '分级预警'],
              ['strategy', '策略建议'],
              ['log', '变更日志'],
            ].map(([k, label]) => (
              <div key={k} className={`page-tab ${activeTab === k ? 'active' : ''}`} onClick={() => setActiveTab(k as TabKey)}>
                {label}
              </div>
            ))}
          </div>
          <div className="divider-v" />
          <MultiSelect single options={monthOptions.map((m, i) => ({ value: m, label: i === 0 ? `当前月 (${m})` : i === 1 ? `上月 (${m})` : m }))} value={[activeMonth]} onChange={(vals) => { setActiveMonth(vals[0] ?? monthOptions[0] ?? ''); setPage(1); }} style={{ minWidth: 160 }} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm">导出报表</button>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>{activeTab === 'channel' ? '新增渠道分配' : '设置预算'}</button>
          </div>
        </div>
      )}


      <div className={`sub-view ${activeTab === 'allocation' ? 'active' : ''}`} id="tab-allocation">
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 10 : 14, marginBottom: 20 }}>
          {(() => { const r = sfFull(stats.totalMonthBudget); return (
          <div className="metric-card mc-blue">
            <div className="metric-label">公司本月总预算</div>
            <div className="metric-value" style={{ color: 'var(--primary)' }}>{r.value ?? '$*'}</div>
            {r.hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{r.hint}</div>}
          </div>); })()}
          {(() => { const r = sfFull(stats.totalMonthSpent); return (
          <div className="metric-card mc-green">
            <div className="metric-label">本月已消耗</div>
            <div className="metric-value" style={{ color: 'var(--green)' }}>{r.value ?? '$*'}</div>
            {r.hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{r.hint}</div>}
          </div>); })()}
          {(() => { const r = sfFull(stats.totalTodaySpent); return (
          <div className="metric-card mc-amber">
            <div className="metric-label">今日已消耗</div>
            <div className="metric-value" style={{ color: 'var(--amber)' }}>{r.value ?? '$*'}</div>
            {r.hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{r.hint}</div>}
          </div>); })()}
          <div className="metric-card"><div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'var(--red)' }} /><div className="metric-label">预警中</div><div className="metric-value" style={{ color: 'var(--red)' }}>{stats.alertCount}</div></div>
        </div>
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">优化师预算分配</div><div className="card-subtitle">按已绑定平台账号的人员分配日/月预算上限，消耗数据来自成效数据</div></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div className="search-box" style={{ width: 200 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                <input placeholder="搜索优化师..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => { setPage(1); void fetchAllocation(); }}>搜索</button>
            </div>
          </div>
          <div className="table-wrap">
            <table style={{ minWidth: 760 }}>
              <thead><tr><th style={{ whiteSpace: 'nowrap' }}>优化师</th><th style={{ whiteSpace: 'nowrap' }}>日预算</th><th style={{ whiteSpace: 'nowrap' }}>今日消耗</th><th style={{ whiteSpace: 'nowrap' }}>日进度</th><th style={{ whiteSpace: 'nowrap' }}>月预算</th><th style={{ whiteSpace: 'nowrap' }}>本月消耗</th><th style={{ whiteSpace: 'nowrap' }}>月进度</th><th style={{ whiteSpace: 'nowrap' }}>预警状态</th><th style={{ whiteSpace: 'nowrap' }}>操作</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={9}><TableLoadingCell /></td></tr>}
                {!loading && list.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text3)' }}>暂无预算数据</td></tr>}
                {!loading && list.map((it) => {
                  const dPct = pct(Number(it.today_spent), Number(it.daily_budget));
                  const mPct = pct(Number(it.month_spent), Number(it.monthly_budget));
                  return (
                    <tr key={it.id}>
                      <td style={{ fontWeight: 600 }}>{it.optimizer}</td>
                      <td style={{ fontWeight: 600 }}>{sf(Number(it.daily_budget))}</td>
                      <td>{sf(Number(it.today_spent))}</td>
                      <td><div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div className="progress" style={{ width: 80 }}><div className="progress-fill" style={{ width: `${Math.min(dPct, 100)}%`, background: pctColor(dPct) }} /></div><span style={{ color: pctColor(dPct), fontSize: 11, fontWeight: 600 }}>{dPct}%</span></div></td>
                      <td style={{ fontWeight: 600 }}>{sf(Number(it.monthly_budget))}</td>
                      <td>{sf(Number(it.month_spent))}</td>
                      <td><div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div className="progress" style={{ width: 80 }}><div className="progress-fill" style={{ width: `${Math.min(mPct, 100)}%`, background: pctColor(mPct) }} /></div><span style={{ fontSize: 11, fontWeight: 600 }}>{mPct}%</span></div></td>
                      <td><span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, fontWeight: 600, background: it.alert_level >= 2 ? 'rgba(239,68,68,.12)' : it.alert_level === 1 ? 'rgba(245,158,11,.1)' : 'rgba(16,185,129,.08)', color: it.alert_level >= 2 ? 'var(--red)' : it.alert_level === 1 ? 'var(--amber)' : 'var(--green)' }}>{it.alert_status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(it)}>编辑</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 10 }} disabled={deletingID === it.id} onClick={() => setConfirmDeleteID(it.id)}>删除</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={Math.max(1, totalPage)} onChange={(p) => setPage(p)} total={`共 ${total} 条`} pageSize={size} pageSizeOptions={[10, 20, 50, 100]} onPageSizeChange={nextSize => { setSize(nextSize); setPage(1); }} disabled={loading} />
        </div>
      </div>

      <div className={`sub-view ${activeTab === 'channel' ? 'active' : ''}`} id="tab-channel">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>选择优化师:</span>
            <MultiSelect single searchable options={[{value:'',label:'全部'},...accountOptions.map((o) => ({value:o.user_name,label:o.user_name}))]} value={[channelOptimizer]} onChange={(vals) => setChannelOptimizer(vals[0] ?? '')} style={{ minWidth: 140 }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowTransferModal(true)}>发起调拨</button>
            <button className="btn btn-primary btn-sm" onClick={() => void saveChannel()}>保存分配</button>
          </div>
        </div>
        {channelOptimizer && (() => {
          const totalBudget = list.find((x) => x.optimizer === channelOptimizer)?.monthly_budget ?? 0;
          const allocated = channelAllocations.filter((x) => x.optimizer === channelOptimizer).reduce((s, x) => s + x.monthly_budget, 0);
          const remaining = totalBudget - allocated;
          return (
            <div style={{ display: 'flex', gap: 24, padding: '12px 16px', background: 'var(--bg2)', borderRadius: 8, marginBottom: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>总预算: <strong style={{ color: 'var(--text1)' }}>{sf(totalBudget)}</strong></span>
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>已分配: <strong style={{ color: allocated > totalBudget ? 'var(--red)' : 'var(--green)' }}>{sf(allocated)}</strong></span>
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>未分配: <strong style={{ color: remaining < 0 ? 'var(--red)' : 'var(--text1)' }}>{sf(remaining)}</strong></span>
              {allocated > totalBudget + 0.01 && (
                <span style={{ fontSize: 12, color: 'var(--red)', marginLeft: 8 }}>⚠ 分配金额超出总预算</span>
              )}
            </div>
          );
        })()}
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr><th>优化师</th><th>平台账号</th><th>日预算</th><th>月预算</th><th>今日消耗</th><th>本月消耗</th><th>ROI</th><th>CPA</th><th>操作</th></tr></thead>
              <tbody>
                {channelAllocations.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text3)' }}>暂无跨渠道分配数据，点击「新增渠道分配」添加</td></tr>}
                {channelAllocations.map((it, idx) => (
                  <tr key={it.id || `${it.optimizer}_${it.channel}_${idx}`}>
                    <td style={{ fontWeight: 600 }}>{it.optimizer}</td>
                    <td>{channelTag(it.channel)}</td>
                    <td><input className="form-input" type="number" value={it.daily_budget} onChange={(e) => setChannelAllocations((prev) => prev.map((p, i) => i === idx ? { ...p, daily_budget: Number(e.target.value || 0) } : p))} /></td>
                    <td><input className="form-input" type="number" value={it.monthly_budget} onChange={(e) => setChannelAllocations((prev) => prev.map((p, i) => i === idx ? { ...p, monthly_budget: Number(e.target.value || 0) } : p))} /></td>
                    <td style={{ color: 'var(--text3)', fontSize: 13 }}>{sf(Number(it.today_spent))}</td>
                    <td style={{ color: 'var(--text3)', fontSize: 13 }}>{sf(Number(it.month_spent))}</td>
                    <td><input className="form-input" type="number" value={it.roi} onChange={(e) => setChannelAllocations((prev) => prev.map((p, i) => i === idx ? { ...p, roi: Number(e.target.value || 0) } : p))} /></td>
                    <td><input className="form-input" type="number" value={it.cpa} onChange={(e) => setChannelAllocations((prev) => prev.map((p, i) => i === idx ? { ...p, cpa: Number(e.target.value || 0) } : p))} /></td>
                    <td><button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 10 }} onClick={() => setConfirmDeleteChannelIdx(idx)}>删除</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <div><div className="card-title">渠道调拨记录</div><div className="card-subtitle">优化师发起的渠道间预算调拨</div></div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowTransferModal(true)}>发起调拨</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>时间</th><th>优化师</th><th>来源渠道</th><th>目标渠道</th><th>金额</th><th>原因</th><th>状态</th><th>操作</th></tr></thead>
              <tbody>
                {transferList.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text3)' }}>暂无调拨记录</td></tr>}
                {transferList.map((t) => (
                  <tr key={t.id}>
                    <td style={{ color: 'var(--text3)' }}>{new Date(t.created_at).toLocaleString()}</td>
                    <td style={{ fontWeight: 600 }}>{t.optimizer}</td>
                    <td>{channelTag(t.from_channel)}</td>
                    <td>{channelTag(t.to_channel)}</td>
                    <td style={{ fontWeight: 600 }}>{money(t.amount)}</td>
                    <td style={{ fontSize: 11 }}>{t.reason}</td>
                    <td>
                      {t.status === 'approved' ? <span className="status status-active" style={{ fontSize: 10 }}>已生效</span> :
                        t.status === 'pending' ? <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(245,158,11,.1)', color: 'var(--amber)', fontWeight: 600 }}>待审批</span> :
                          <span style={{ fontSize: 10, color: 'var(--red)' }}>已拒绝</span>}
                    </td>
                    <td>
                      {t.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--green)', fontSize: 10 }} onClick={() => void updateTransferStatus(t.id, 'approved')}>批准</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 10 }} onClick={() => void updateTransferStatus(t.id, 'rejected')}>拒绝</button>
                        </div>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={`sub-view ${activeTab === 'alert' ? 'active' : ''}`} id="tab-alert">
        <div className="grid-3 mb-24">
          <div className="card" style={{ borderLeft: '4px solid var(--amber)', padding: 16 }}><div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--amber)' }}>一级预警 · 提醒</div><div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>阈值: {alertRule.level1_threshold}%</div></div>
          <div className="card" style={{ borderLeft: '4px solid rgb(249,115,22)', padding: 16 }}><div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'rgb(249,115,22)' }}>二级预警 · 警告</div><div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>阈值: {alertRule.level2_threshold}%</div></div>
          <div className="card" style={{ borderLeft: '4px solid var(--red)', padding: 16 }}><div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--red)' }}>三级预警 · 熔断</div><div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>阈值: {alertRule.level3_threshold}%</div></div>
        </div>
        <div className="card mb-24">
          <div className="card-header">
            <div><div className="card-title">当前预警事件</div></div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowRuleModal(true)}>编辑预警规则</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>优化师</th><th>预警级别</th><th>触发维度</th><th>消耗/上限</th><th>系统动作</th><th>处理</th></tr></thead>
              <tbody>
                {currentAlerts.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text3)' }}>当前无预警</td></tr>}
                {currentAlerts.map((it) => <tr key={it.id}><td style={{ fontWeight: 600 }}>{it.optimizer}</td><td>{it.level}</td><td>{it.trigger_dim}</td><td>{money(it.spent)} / {money(it.budget)}</td><td>{it.action}</td><td>{it.result}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">预警历史 (近 7 天)</div></div>
            <MultiSelect single options={[{value:'',label:'全部级别'},{value:'1',label:'一级提醒'},{value:'2',label:'二级警告'},{value:'3',label:'三级熔断'}]} value={[alertFilterLevel]} onChange={(vals) => setAlertFilterLevel(vals[0] ?? '')} />
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>时间</th><th>优化师</th><th>预警级别</th><th>触发维度</th><th>消耗/上限</th><th>系统动作</th><th>处理</th></tr></thead>
              <tbody>
                {historyAlerts.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)' }}>暂无预警历史</td></tr>}
                {historyAlerts.map((it) => <tr key={it.id}><td style={{ color: 'var(--text3)' }}>{new Date(it.created_at).toLocaleString()}</td><td style={{ fontWeight: 600 }}>{it.optimizer}</td><td>{it.level}</td><td>{it.trigger_dim}</td><td>{money(it.spent)} / {money(it.budget)}</td><td>{it.action}</td><td>{it.result}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={`sub-view ${activeTab === 'strategy' ? 'active' : ''}`} id="tab-strategy">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>当前策略模式:</span>
          <div className="page-tabs" id="stratTabs">
            {['roi', 'cost', 'explore'].map((m) => (
              <div key={m} className={`page-tab ${strategyMode === m ? 'active' : ''}`} onClick={() => setStrategyMode(m)}>
                {m === 'roi' ? 'ROI 优先' : m === 'cost' ? '成本控制' : '均衡探索'}
              </div>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>策略引擎每日 00:30 自动运行 · 基于 7 天数据</div>
        </div>
        <div>
          {strategies.length === 0 && <div className="card"><div className="card-subtitle">暂无策略建议</div></div>}
          {strategies.map((s, idx) => {
            let rec: Record<string, number> = {};
            try {
              rec = JSON.parse(s.recommendation_json || '{}') as Record<string, number>;
            } catch {
              rec = {};
            }
            const source = (channelByOptimizer.get(s.optimizer) || []).reduce<Record<string, number>>((acc, it) => {
              acc[it.channel.toLowerCase()] = Number(it.daily_budget || 0);
              return acc;
            }, {});
            const borderColor = idx % 2 === 0 ? 'var(--green)' : 'var(--amber)';
            return (
              <div key={s.id} className="card" style={{ borderLeft: `4px solid ${borderColor}`, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: idx % 2 === 0 ? 'var(--green-bg)' : 'rgba(245,158,11,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{idx % 2 === 0 ? '📈' : '⚠️'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{s.title || `${s.optimizer} · 策略建议`}</span>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: idx % 2 === 0 ? 'var(--green-bg)' : 'rgba(245,158,11,.08)', color: idx % 2 === 0 ? 'var(--green)' : 'var(--amber)', fontWeight: 600 }}>{strategyMode === 'roi' ? 'ROI 优先' : strategyMode === 'cost' ? '成本控制' : '均衡探索'}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)' }}>今天 00:30 生成</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 12 }}>{s.summary || '基于近 7 天数据，系统建议优化预算分配。'}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 10, marginBottom: 12 }}>
                      {['google', 'facebook', 'tiktok'].map((ch) => {
                        const from = Number(source[ch] || 0);
                        const to = Number(rec[ch] || from);
                        const diff = from > 0 ? Math.round(((to - from) / from) * 100) : 0;
                        const color = diff > 0 ? 'var(--green)' : diff < 0 ? 'var(--red)' : 'var(--text3)';
                        return (
                          <div key={`${s.id}_${ch}`} style={{ padding: 10, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', fontSize: 12 }}>
                            <div style={{ marginBottom: 4 }}>{channelTag(ch)}</div>
                            {money(from)} <strong style={{ color }}>{money(to)}</strong> <span style={{ color, fontSize: 11 }}>({diff >= 0 ? '+' : ''}{diff}%)</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding: 10, background: 'var(--green-bg)', borderRadius: 'var(--radius-sm)', fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>
                      <strong style={{ color: 'var(--green)' }}>预期:</strong> ROAS {Math.max(0, (Number(s.expected_roi || 0) - 0.2)).toFixed(1)}x -&gt; {Number(s.expected_roi || 0).toFixed(1)}x，日均利润 +{money(s.expected_profit_delta)}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => void adoptStrategy(s.id)}>一键采纳</button>
                      <button className="btn btn-outline btn-sm" onClick={() => setActiveTab('channel')}>微调后应用</button>
                      <button className="btn btn-ghost btn-sm">忽略</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><div><div className="card-title">策略效果追踪</div><div className="card-subtitle">已采纳建议的 T+1 / T+7 效果回溯</div></div></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>日期</th><th>优化师</th><th>策略</th><th>调整</th><th>T+1</th><th>T+7</th><th>评估</th></tr></thead>
              <tbody>
                {effects.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text3)' }}>暂无效果数据</td></tr>}
                {effects.map((e) => <tr key={e.id}><td style={{ color: 'var(--text3)' }}>{new Date(e.created_at).toLocaleDateString()}</td><td style={{ fontWeight: 600 }}>{e.optimizer}</td><td>{e.strategy_mode}</td><td style={{ fontSize: 11 }}>{e.adjustment}</td><td style={{ fontWeight: 600 }}>{Number(e.t1_roi).toFixed(1)}x</td><td style={{ fontWeight: 600 }}>{Number(e.t7_roi).toFixed(1)}x</td><td><span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: e.evaluation === '达成' ? 'var(--green-bg)' : 'rgba(245,158,11,.08)', color: e.evaluation === '达成' ? 'var(--green)' : 'var(--amber)', fontWeight: 700 }}>{e.evaluation}</span></td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={`sub-view ${activeTab === 'log' ? 'active' : ''}`} id="tab-log">
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">预算变更日志</div><div className="card-subtitle">所有预算操作的完整审计记录</div></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <MultiSelect single options={[{value:'',label:'全部操作'},{value:'adjust',label:'预算调整'},{value:'transfer',label:'渠道调拨'},{value:'strategy',label:'策略采纳'},{value:'alert',label:'预警触发'},{value:'freeze',label:'冻结/恢复'}]} value={[logType]} onChange={(vals) => { setLogType(vals[0] ?? ''); setLogPage(1); }} />
              <button className="btn btn-outline btn-sm">导出</button>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>时间</th><th>操作人</th><th>操作类型</th><th>目标</th><th>变更详情</th><th>原因</th></tr></thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text3)' }}>暂无日志</td></tr>}
                {logs.map((l) => <tr key={l.id}><td style={{ color: 'var(--text3)' }}>{new Date(l.created_at).toLocaleString()}</td><td style={{ fontWeight: 600 }}>{l.operator}</td><td><span style={{ color: actionTypeColor(l.action_type), fontWeight: 600 }}>{actionTypeText(l.action_type)}</span></td><td>{l.target}</td><td style={{ fontSize: 11 }}>{l.detail}</td><td style={{ fontSize: 11, color: 'var(--text3)' }}>{l.reason || '-'}</td></tr>)}
              </tbody>
            </table>
          </div>
          <Pagination page={logPage} totalPages={Math.max(1, logTotalPage)} onChange={(p) => setLogPage(p)} total={`共 ${logTotal} 条记录`} pageSize={logSize} pageSizeOptions={[10, 20, 50, 100]} onPageSizeChange={nextSize => { setLogSize(nextSize); setLogPage(1); }} />
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay open" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>{editing ? '编辑预算' : '设置预算'}</h3><button className="modal-close" onClick={() => !saving && setShowModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">优化师</label>
                  <MultiSelect
                    single
                    searchable
                    options={[
                      ...accountOptions.map((opt) => ({ value: opt.user_name, label: opt.user_name })),
                      ...(editing && !accountOptions.find((o) => o.user_name === form.optimizer) && form.optimizer ? [{ value: form.optimizer, label: form.optimizer }] : []),
                    ]}
                    value={form.optimizer ? [form.optimizer] : []}
                    onChange={(vals) => setForm((f) => ({ ...f, optimizer: vals[0] ?? '' }))}
                    placeholder="请选择人员"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">月份</label>
                  <MultiSelect
                    single
                    options={monthOptions.map((m, i) => ({ value: m, label: i === 0 ? `当前月 (${m})` : i === 1 ? `上月 (${m})` : m }))}
                    value={form.month ? [form.month] : []}
                    onChange={(vals) => setForm((f) => ({ ...f, month: vals[0] ?? '' }))}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">日预算上限 ($)</label><input className="form-input" type="number" min="0" value={form.daily_budget} onChange={(e) => setForm((f) => ({ ...f, daily_budget: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">月预算上限 ($)</label><input className="form-input" type="number" min="0" value={form.monthly_budget} onChange={(e) => setForm((f) => ({ ...f, monthly_budget: e.target.value }))} /></div>
              </div>
              {editing && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" style={{ color: 'var(--text3)' }}>今日消耗（只读，来自成效数据）</label>
                    <input className="form-input" type="text" readOnly value={sf(editing.today_spent)} style={{ background: 'var(--surface2)', color: 'var(--text3)', cursor: 'not-allowed' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ color: 'var(--text3)' }}>本月消耗（只读，来自成效数据）</label>
                    <input className="form-input" type="text" readOnly value={sf(editing.month_spent)} style={{ background: 'var(--surface2)', color: 'var(--text3)', cursor: 'not-allowed' }} />
                  </div>
                </div>
              )}
              <div className="form-group"><label className="form-label">备注</label><textarea className="form-textarea" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" disabled={saving} onClick={() => setShowModal(false)}>取消</button><button className="btn btn-primary" disabled={saving} onClick={() => void submitAllocation()}>{saving ? '保存中...' : '保存'}</button></div>
          </div>
        </div>
      )}

      {showTransferModal && (
        <div className="modal-overlay open" onClick={() => setShowTransferModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>发起渠道调拨</h3><button className="modal-close" onClick={() => setShowTransferModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">优化师</label><MultiSelect single searchable options={channelAllocOptimizers.map((name) => ({ value: name, label: name }))} value={transferForm.optimizer ? [transferForm.optimizer] : []} onChange={(vals) => setTransferForm((f) => ({ ...f, optimizer: vals[0] ?? '', from_channel: '', to_channel: '' }))} placeholder="请选择" /></div>
              {transferChannels.length === 0 && transferForm.optimizer && (
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>加载渠道中...</div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">来源渠道</label>
                  <MultiSelect
                    single
                    options={transferChannels.map((c) => ({ value: c.channel, label: channelName[c.channel] || c.channel }))}
                    value={transferForm.from_channel ? [transferForm.from_channel] : []}
                    onChange={(vals) => setTransferForm((f) => ({ ...f, from_channel: vals[0] ?? '' }))}
                    placeholder="请选择"
                  />
                  {transferForm.from_channel && (() => {
                    const opt = transferChannels.find((c) => c.channel === transferForm.from_channel);
                    if (!opt || opt.monthly_budget <= 0) return null;
                    return <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>可用余额: <strong style={{ color: 'var(--green)' }}>{sf(opt.available)}</strong> (月预算 {sf(opt.monthly_budget)} - 已消耗 {sf(opt.month_spent)})</div>;
                  })()}
                </div>
                <div className="form-group">
                  <label className="form-label">目标渠道</label>
                  <MultiSelect
                    single
                    options={transferChannels.filter((c) => c.channel !== transferForm.from_channel).map((c) => ({ value: c.channel, label: channelName[c.channel] || c.channel }))}
                    value={transferForm.to_channel ? [transferForm.to_channel] : []}
                    onChange={(vals) => setTransferForm((f) => ({ ...f, to_channel: vals[0] ?? '' }))}
                    placeholder="请选择"
                  />
                </div>
              </div>
              <div className="form-group"><label className="form-label">调拨金额</label><input className="form-input" type="number" min="0" value={transferForm.amount} onChange={(e) => setTransferForm((f) => ({ ...f, amount: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">原因</label><textarea className="form-textarea" value={transferForm.reason} onChange={(e) => setTransferForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => setShowTransferModal(false)}>取消</button><button className="btn btn-primary" disabled={!transferForm.optimizer || !transferForm.from_channel || !transferForm.to_channel} onClick={() => void createTransfer()}>提交</button></div>
          </div>
        </div>
      )}

      {showChannelAddModal && (() => {
        const budgetAlloc = list.find((x) => x.optimizer === channelForm.optimizer);
        const existing = channelAllocations.filter((x) => x.optimizer === channelForm.optimizer);
        const usedChannels = new Set(existing.map((x) => x.channel));
        const availableChannels = ALL_CHANNELS.filter((c) => !usedChannels.has(c));
        const sumMonthly = existing.reduce((s, x) => s + Number(x.monthly_budget), 0);
        const sumDaily = existing.reduce((s, x) => s + Number(x.daily_budget), 0);
        return (
          <div className="modal-overlay open" onClick={() => !channelSaving && setShowChannelAddModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>新增渠道分配</h3>
                <button className="modal-close" onClick={() => !channelSaving && setShowChannelAddModal(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">优化师</label>
                  <MultiSelect
                    single
                    options={list.map((o) => ({ value: o.optimizer, label: o.optimizer }))}
                    value={channelForm.optimizer ? [channelForm.optimizer] : []}
                    onChange={(vals) => setChannelForm((f) => ({ ...f, optimizer: vals[0] ?? '', channel: '' }))}
                    placeholder={list.length === 0 ? '暂无已设置预算的人员' : '请选择优化师'}
                  />
                  {channelForm.optimizer && !budgetAlloc && (
                    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>该优化师尚未设置预算分配，请先在「预算分配」Tab 中添加</div>
                  )}
                  {budgetAlloc && (
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                      总日预算 <strong>{money(budgetAlloc.daily_budget)}</strong>（已分配 {money(sumDaily)}，剩余 <strong style={{ color: Number(budgetAlloc.daily_budget) - sumDaily < 0 ? 'var(--red)' : 'var(--green)' }}>{money(Number(budgetAlloc.daily_budget) - sumDaily)}</strong>）
                      &nbsp;·&nbsp;
                      总月预算 <strong>{money(budgetAlloc.monthly_budget)}</strong>（已分配 {money(sumMonthly)}，剩余 <strong style={{ color: Number(budgetAlloc.monthly_budget) - sumMonthly < 0 ? 'var(--red)' : 'var(--green)' }}>{money(Number(budgetAlloc.monthly_budget) - sumMonthly)}</strong>）
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">渠道</label>
                    <MultiSelect
                      single
                      options={availableChannels.map((c) => ({ value: c, label: channelName[c] || c }))}
                      value={channelForm.channel ? [channelForm.channel] : []}
                      onChange={(vals) => setChannelForm((f) => ({ ...f, channel: vals[0] ?? '' }))}
                      placeholder="请选择渠道"
                    />
                    {channelForm.optimizer && availableChannels.length === 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>该优化师的所有渠道已添加完毕</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">月份</label>
                    <MultiSelect
                      single
                      options={monthOptions.map((m, i) => ({ value: m, label: i === 0 ? `当前月 (${m})` : i === 1 ? `上月 (${m})` : m }))}
                      value={channelForm.month ? [channelForm.month] : []}
                      onChange={(vals) => setChannelForm((f) => ({ ...f, month: vals[0] ?? '' }))}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">日预算 ($)</label>
                    <input className="form-input" type="number" min="0" value={channelForm.daily_budget} onChange={(e) => setChannelForm((f) => ({ ...f, daily_budget: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">月预算 ($)</label>
                    <input className="form-input" type="number" min="0" value={channelForm.monthly_budget} onChange={(e) => setChannelForm((f) => ({ ...f, monthly_budget: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">目标 ROI</label>
                    <input className="form-input" type="number" min="0" step="0.1" value={channelForm.roi} onChange={(e) => setChannelForm((f) => ({ ...f, roi: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">目标 CPA ($)</label>
                    <input className="form-input" type="number" min="0" value={channelForm.cpa} onChange={(e) => setChannelForm((f) => ({ ...f, cpa: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" disabled={channelSaving} onClick={() => setShowChannelAddModal(false)}>取消</button>
                <button className="btn btn-primary" disabled={channelSaving} onClick={() => void addChannelRow()}>{channelSaving ? '保存中...' : '保存'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmDeleteChannelIdx !== null && (
        <div className="modal-overlay open" onClick={() => setConfirmDeleteChannelIdx(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>确认删除</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteChannelIdx(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(239,68,68,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" width="20" height="20"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                    确认删除 {channelTag(channelAllocations[confirmDeleteChannelIdx]?.channel)} 渠道分配吗？
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>删除后该优化师在此渠道的预算配置将被清除，不可恢复。</div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDeleteChannelIdx(null)}>取消</button>
              <button className="btn btn-primary" style={{ background: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => void deleteChannelRow(confirmDeleteChannelIdx)}>确认删除</button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteID && (
        <div className="modal-overlay open" onClick={() => setConfirmDeleteID(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>确认删除</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteID(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(239,68,68,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" width="20" height="20"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>确认删除该预算记录吗？</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>删除后不可恢复，该优化师的预算配置及渠道分配记录将一并清除。</div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDeleteID(null)}>取消</button>
              <button className="btn btn-primary" style={{ background: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => void remove()}>确认删除</button>
            </div>
          </div>
        </div>
      )}

      {showRuleModal && (
        <div className="modal-overlay open" onClick={() => setShowRuleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>预警规则配置</h3><button className="modal-close" onClick={() => setShowRuleModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label className="form-label">一级阈值</label><input className="form-input" type="number" value={alertRule.level1_threshold} onChange={(e) => setAlertRule((r) => ({ ...r, level1_threshold: Number(e.target.value || 0) }))} /></div>
                <div className="form-group"><label className="form-label">二级阈值</label><input className="form-input" type="number" value={alertRule.level2_threshold} onChange={(e) => setAlertRule((r) => ({ ...r, level2_threshold: Number(e.target.value || 0) }))} /></div>
                <div className="form-group"><label className="form-label">三级阈值</label><input className="form-input" type="number" value={alertRule.level3_threshold} onChange={(e) => setAlertRule((r) => ({ ...r, level3_threshold: Number(e.target.value || 0) }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">静默开始</label><input className="form-input" value={alertRule.quiet_start} onChange={(e) => setAlertRule((r) => ({ ...r, quiet_start: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">静默结束</label><input className="form-input" value={alertRule.quiet_end} onChange={(e) => setAlertRule((r) => ({ ...r, quiet_end: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label style={{ display: 'flex', gap: 6 }}><input type="checkbox" checked={alertRule.auto_pause} onChange={(e) => setAlertRule((r) => ({ ...r, auto_pause: e.target.checked }))} /> 自动暂停</label></div>
              <div className="form-group"><label style={{ display: 'flex', gap: 6 }}><input type="checkbox" checked={alertRule.channel_level_enabled} onChange={(e) => setAlertRule((r) => ({ ...r, channel_level_enabled: e.target.checked }))} /> 渠道独立预警</label></div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => setShowRuleModal(false)}>取消</button><button className="btn btn-primary" onClick={() => void saveAlertRule()}>保存</button></div>
          </div>
        </div>
      )}
      {/* ── Toast stack ─────────────────────────────────────────────────── */}
      <div style={{ position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
        {toasts.map((t) => {
          const c = TOAST_COLOR[t.type];
          return (
            <div key={t.id} style={{
              pointerEvents: 'auto',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10,
              padding: '11px 16px', fontSize: 13, fontWeight: 500, color: c.color,
              boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
              animation: 'fadeIn 0.2s ease',
              minWidth: 260, maxWidth: 460, wordBreak: 'break-word',
            }}>
              <ToastIcon type={t.type} />
              <span style={{ flex: 1, lineHeight: 1.5 }}>{t.message}</span>
              <button onClick={() => dismissToast(t.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.55, padding: '0 0 0 4px', fontSize: 17, lineHeight: 1, flexShrink: 0, marginTop: -1 }}>
                ×
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
